// web/src/i18n/LanguageBadge.tsx
//
// "Available in <language>" indicator for content surfaces where the
// requested language wasn't available and the API fell back to another
// (in practice: English fallback when no translated row exists yet).
//
// Renders nothing in the happy path (`requested === served`) so consumers
// can drop it next to a title without conditional wrapping at every
// callsite. Visually subtle: muted shadcn <Badge variant="secondary"> with
// the lucide Languages glyph — informative but never load-bearing.
//
// Wired from BriefingsModal (category briefings), StickySignalCard via
// Pane (per-material analyses), and EventPanel (calendar events). The
// requested language comes from useLanguage().language; the served
// language is on every content envelope from the API
// (BriefingResponse.language_served, MaterialAnalysisResponse, …).
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Languages } from "@/components/icons";
import { languagesApi, type LanguagesResponse } from "@/api/languages";

interface LanguageBadgeProps {
  /** The language the caller requested (e.g. useLanguage().language). */
  requestedLanguage: string;
  /** The language the API actually returned (envelope.language_served). */
  servedLanguage: string;
  /** Optional class additions for caller-specific positioning. */
  className?: string;
}

/**
 * Look up a human-readable label for a language code. Prefers the
 * operator-curated display_name from /api/v1/languages; falls back to the
 * raw code if the languages payload isn't cached yet (the badge stays
 * informative even before the list resolves).
 */
function useLanguageLabel(code: string): string {
  // Shares the cache key with LanguageProvider + LanguageSelect so this
  // is essentially a free read once those have mounted.
  const { data } = useQuery<LanguagesResponse>({
    queryKey: ["languages"],
    queryFn: () => languagesApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const entry = data?.languages.find((l) => l.code === code);
  if (entry?.display_name) return entry.display_name;
  // Last-resort capitalized code so we still render a sane label
  // pre-cache (e.g. "EN" instead of "en").
  return code.toUpperCase();
}

export function LanguageBadge({
  requestedLanguage,
  servedLanguage,
  className,
}: LanguageBadgeProps) {
  const { t } = useTranslation("common");
  const servedLabel = useLanguageLabel(servedLanguage);

  // Happy path — nothing to advertise.
  if (requestedLanguage === servedLanguage) return null;

  return (
    <Badge
      variant="secondary"
      className={className}
      aria-label={t("i18n.fallbackBadgeAria", { language: servedLabel })}
      data-testid="language-fallback-badge"
    >
      <Languages aria-hidden="true" />
      {t("i18n.fallbackBadge", { language: servedLabel })}
    </Badge>
  );
}
